/**
 * InboxPersonGroup — B1 (spec 2026-08-12 §3.3) + Alvo C round2 (mockup 02/2a):
 * o card agrupado por pessoa com CABEÇALHO FORTE.
 *
 * Cabeçalho: avatar 36px + nome + trilha de episódios (sempre — decisão 2 da
 * rodada) + «N conversas no total» REAL (meta.totalCount do endpoint da ficha
 * — nunca a contagem da página, a mentira medida no teste do TL). Rodapé:
 * «+ N anteriores — ver no histórico». As linhas seguem cards `compact`
 * intactos (sinais de urgência/entrega/IA por conversa — [3.726]).
 *
 * Dados do histórico: `usePersonConversations` (mesmo cache da ficha/painel;
 * fetch GATED — cap `person-read` e personId, regra «gatear o fetch»). Sem a
 * cap ou com o fetch em erro, o cabeçalho mostra só a identidade — NUNCA
 * «N nesta lista».
 *
 * Só é renderizado para grupos com 2+ conversas — pessoa com 1 conversa usa o
 * ConversationCard2B puro (o caso comum não paga cerimônia de grupo).
 * Estilos nas classes `.inbox-person-group*` (inbox-2b.css).
 */
import { History } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { UserAvatar } from '../../components/UserAvatar';
import { useCapability } from '../../hooks/use-capability';
import { usePersonConversations } from '../persons/use-persons';

import { ConversationCard2B } from './ConversationCard2B';
import { EpisodeTrail } from './EpisodeTrail';

import type { PersonGroup } from '@lume/shared';

export function InboxPersonGroup({
  group,
  selectedId,
  onSelect,
  onClaim,
}: {
  group: PersonGroup;
  selectedId?: string | null;
  onSelect?: (id: string) => void;
  onClaim?: (id: string) => void;
}): JSX.Element {
  const navigate = useNavigate();
  // P19 — o destino do link é a ficha da pessoa (gate `person-read`, o mesmo
  // do atalho do header da frente C). Sem a cap, o botão SOME — nunca beco.
  const canViewPerson = useCapability('person-read');
  const name = group.person?.displayName ?? 'Cliente';
  const personId = group.personId;

  // Alvo C — histórico REAL da pessoa (total + episódios da trilha), gated.
  const { data } = usePersonConversations(
    personId ?? undefined,
    canViewPerson && personId !== null,
  );
  const totalCount = data?.meta.totalCount;
  const episodes = data?.data ?? [];
  const priorCount =
    totalCount !== undefined ? Math.max(0, totalCount - group.items.length) : undefined;

  const goHistory = (): void => {
    navigate(`/pessoas/${personId ?? ''}#conversas`);
  };

  return (
    <div className="inbox-person-group" data-testid={`person-group-${group.key}`}>
      <div className="inbox-person-group__head">
        <UserAvatar name={name} colorKey={personId ?? group.key} size="md" />
        <div className="inbox-person-group__id">
          <span className="inbox-person-group__name">{name}</span>
          <span className="inbox-person-group__meta">
            <EpisodeTrail
              episodes={episodes}
              totalCount={totalCount ?? episodes.length}
              onOpen={(id) => navigate(`/atendimento/conversation/${id}`)}
            />
            {totalCount !== undefined && (
              <span className="inbox-person-group__total">
                <strong>{totalCount}</strong> no total
              </span>
            )}
          </span>
        </div>
        <span className="inbox-person-group__spacer" />
        {personId !== null && canViewPerson && (
          <button
            type="button"
            className="btn btn--secondary btn--sm inbox-person-group__history"
            onClick={goHistory}
          >
            <History size={12} aria-hidden /> Histórico →
          </button>
        )}
      </div>
      {group.items.map((c) => (
        <ConversationCard2B
          key={c.id}
          conversation={c}
          selected={c.id === selectedId}
          compact
          {...(onSelect !== undefined && { onSelect })}
          {...(onClaim !== undefined && { onClaim })}
        />
      ))}
      {priorCount !== undefined && priorCount > 0 && canViewPerson && (
        <button type="button" className="inbox-person-group__foot" onClick={goHistory}>
          + {priorCount} conversa{priorCount === 1 ? '' : 's'} anterior
          {priorCount === 1 ? '' : 'es'} — ver no histórico
        </button>
      )}
    </div>
  );
}
